import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { SearchX } from 'lucide-react'
import { EmptyState } from '@/components/ui/empty-state'
import { useClientDashboard, useClientLinearIssues } from '@/queries/clientDashboards'
import { OPEN_HUB_STATUSES } from '@/lib/linearStatus'
import { useTrackerTickets } from '@/queries/tracker'
import { cn } from '@/lib/utils'
import { ClientDashboardHeader } from './ClientDashboardHeader'
import { LoadError } from './LoadError'
import { MeetingPrepDialog } from './MeetingPrepDialog'
import { CountChip } from './dashboardParts'
import { CLIENT_TABS, CLIENTS_PAGE, clientTabPath, openTrackerParams } from './tabs'

const isNotFound = (error: unknown) => {
  const status = (error as { statusCode?: number; status?: number } | null)?.statusCode ?? (error as { status?: number } | null)?.status
  return status === 404
}

export function ClientDashboardLayout({ companyId }: { companyId: string }) {
  const { data, error, isLoading, refetch } = useClientDashboard(companyId)
  const path = useRouterState({ select: state => state.location.pathname })
  const prepOpen = useRouterState({ select: state => Boolean((state.location.search as { prep?: unknown } | undefined)?.prep) })
  const tracker = useTrackerTickets(openTrackerParams(companyId), undefined, { enabled: true })
  const linear = useClientLinearIssues(companyId)
  const linearIssues = linear.data?.data
  const counts: Partial<Record<string, number>> = {
    'client-tracker': tracker.data?.total,
    'linear-tickets': linearIssues?.linked ? linearIssues.issues.filter(issue => OPEN_HUB_STATUSES.includes(issue.status)).length : undefined,
  }

  if (isNotFound(error)) {
    return (
      <EmptyState
        icon={<SearchX />}
        title="This client has no dashboard"
        body="It may have been removed. Add it again from Client dashboards to bring back its settings."
        cta={{ label: 'Back to Client dashboards', to: '/clients' }}
      />
    )
  }
  if (error && !data) return <LoadError title="This dashboard didn't load" onRetry={() => refetch()} backToList />
  if (isLoading || !data) return null

  return (
    <div style={CLIENTS_PAGE}>
      <ClientDashboardHeader dashboard={data.data} />
      <nav aria-label="Client dashboard" className="mb-[18px] flex flex-wrap gap-1 border-b border-etn-border-hairline pb-2.5">
        {CLIENT_TABS.map(tab => {
          const to = clientTabPath(companyId, tab.slug)
          const active = path === to
          const count = counts[tab.slug]
          return (
            <Link
              key={tab.slug}
              to={to as never}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'inline-flex h-[30px] items-center gap-1.5 rounded-etn-pill px-[13px] text-[13px] font-semibold',
                active ? 'bg-etn-accent-tint text-etn-accent-text-active' : 'text-etn-text-label hover:bg-[var(--etn-gray-75)]'
              )}
            >
              {tab.label}
              {count !== undefined && <CountChip>{count}</CountChip>}
            </Link>
          )
        })}
      </nav>
      <Outlet />
      {prepOpen && <MeetingPrepDialog companyId={companyId} clientName={data.data.companyName} />}
    </div>
  )
}
